'use client'

import { useSiteConfig } from '@/feature/config/hooks/useSiteConfig.client'
import { useIsAuthenticated } from '@/lib/auth/client/AuthProvider.client'
import { twMerge } from '@/lib/classname/twMerge'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { useNextIsClientHydrated } from '@/lib/next/useNextIsClientHydrated'
import { Icon } from '@/ui/icons/icon/Icon'
import { Divider } from '@/ui/layouts/divider/Divider'
import { gql } from '@gql-generated/gql'
import type { ProductAvailabilities_PickupPointOfSaleFragment } from '@gql-generated/graphql'
import { useMemo } from 'react'
import type { ProductAvailabilitiesProduct } from '../../types/mergedProduct'
import { getProductAvailabilitiesViews } from '../../utils/productAvailabilities'
import { getOnlineItems, getStoreItems } from '../../utils/productAvailabilityItem'
import { ProductAvailabilitiesSkeleton } from './ProductAvailabilitiesSkeleton'
import { ProductAvailability } from './ProductAvailability.client'
import { ProductStatusLine } from './ProductStatusLine.client'

gql(/* GraphQL */ `
  fragment ProductAvailabilities_Product on Product {
    ...FullProductAvailabilityItem_Availability
  }
`)

gql(/* GraphQL */ `
  fragment ProductAvailabilities_PointOfSaleList on User {
    favoritePointOfSale {
      ...ProductAvailabilities_PickupPointOfSale
    }
  }

  fragment ProductAvailabilities_PickupPointOfSale on PointOfSale {
    id
    name
  }
`)

type ProductAvailabilitiesProps = {
  pickupPointOfSale?: ProductAvailabilities_PickupPointOfSaleFragment | null
  product: ProductAvailabilitiesProduct
  isClarityDelayEnabled?: boolean
  isTitleVisible?: boolean
  isHideOnline?: boolean
  isHideStore?: boolean
  className?: string
  dividerClassName?: string
  isClickAndCollectEnabled?: boolean
  shortMode?: boolean
  handleCountAvailabilityDisplayed?: (availability: number) => void
  notificationClassName?: string
  selectedQuantity?: number
}

export function ProductAvailabilities({
  pickupPointOfSale,
  product,
  isClarityDelayEnabled = false,
  isTitleVisible = true,
  isHideOnline = false,
  isHideStore = false,
  className,
  dividerClassName,
  isClickAndCollectEnabled,
  shortMode = false,
  notificationClassName,
  selectedQuantity = -1,
}: ProductAvailabilitiesProps) {
  const isAuthenticated = useIsAuthenticated()
  const { Trans } = useTranslation(['productAvailabilities', 'checkout'])
  const { config } = useSiteConfig()

  // Defer rendering until mount to avoid hydration mismatches.
  const isMounted = useNextIsClientHydrated()

  const { isOnlineHiddenByData, isStoreHiddenByData } = getProductAvailabilitiesViews({ product })
  const isOnlineVisible =
    !isHideOnline &&
    !isOnlineHiddenByData &&
    (isAuthenticated || !!config?.catalog?.isPublicStockEnabled)
  const isStoreVisible =
    !isHideStore && !isStoreHiddenByData && isClickAndCollectEnabled && isAuthenticated

  const showDivider = isOnlineVisible && isStoreVisible && isTitleVisible

  const onlineItems = useMemo(
    () =>
      getOnlineItems({
        product,
        selectedQuantity,
        isClarityDelayEnabled,
      }),
    [product, selectedQuantity, isClarityDelayEnabled],
  )

  const storeItems = useMemo(
    () =>
      getStoreItems({
        product,
        shortMode,
        isClickAndCollectEnabled: Boolean(isClickAndCollectEnabled),
        selectedQuantity,
      }),
    [product, shortMode, isClickAndCollectEnabled, selectedQuantity],
  )

  if (!isMounted) {
    return (
      <ProductAvailabilitiesSkeleton
        offset={[
          { nbOfLine: 1, title: true },
          { nbOfLine: 1, title: true },
        ]}
      />
    )
  }

  return (
    <div className={className}>
      {isOnlineVisible && (
        <ProductAvailability
          title={
            <Trans
              i18nKey="productAvailabilities:online.title"
              components={{
                uppercase: <span className="uppercase" />,
              }}
            />
          }
          TitleIcon={<Icon.Truck size="sm" />}
          isTitleVisible={isTitleVisible}
        >
          {onlineItems.map(({ id, props }) => (
            <ProductStatusLine
              key={id}
              {...props}
              shortMode={shortMode}
              notificationClassName={notificationClassName}
            />
          ))}
        </ProductAvailability>
      )}
      {showDivider && (
        <div className={twMerge('my-2', dividerClassName)}>
          <Divider direction="horizontal" variant="line" />
        </div>
      )}
      {isStoreVisible && (
        <ProductAvailability
          title={
            <Trans
              i18nKey={
                pickupPointOfSale?.name
                  ? 'productAvailabilities:store.titleWithPointOfSaleName'
                  : 'productAvailabilities:store.title'
              }
              values={pickupPointOfSale ?? undefined}
              components={{
                uppercase: <span className="uppercase" />,
                pointOfSale: <span />,
              }}
            />
          }
          TitleIcon={<Icon.Store size="sm" />}
          isTitleVisible={isTitleVisible}
        >
          {storeItems.map(({ id, props }) => (
            <ProductStatusLine
              key={id}
              {...props}
              shortMode={shortMode}
              notificationClassName={notificationClassName}
            />
          ))}
        </ProductAvailability>
      )}
    </div>
  )
}
